import React from "react";
import { Box, TextField, Typography } from "@mui/material";
import { Controller, type Control, type FieldErrors } from "react-hook-form";
import { Label } from "@/components/Label";
import {
  preventNonNumericValueWhenKeyDown,
  preventNonNumericValueWhenPaste,
} from "@/utils/utils";
import { type FormData } from "../types/formTypes";

interface Props {
  control: Control<FormData>;
  name: keyof FormData;
  label: string;
  unit?: string;
  errors: FieldErrors<FormData>;
  inputProps?: object;
}

export const RangeInput: React.FC<Props> = ({
  control,
  name,
  label,
  unit,
  errors,
  inputProps,
}) => {
  const fieldError = errors[name] as
    | { min?: { message?: string }; max?: { message?: string } }
    | undefined;

  return (
    <>
      <Label>{label}</Label>
      <Controller
        control={control}
        name={name}
        render={({ field: { value, onChange }, fieldState }) => {
          const range = value as { min: number | null; max: number | null };

          return (
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Box
                sx={{
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "flex-start",
                  gap: 1,
                  width: "400px",
                }}
              >
                <TextField
                  error={Boolean(fieldError?.min ?? fieldState.error)}
                  helperText={
                    fieldError?.min?.message ?? fieldState.error?.message
                  }
                  value={range.min ?? ""}
                  onChange={(e) => {
                    onChange({
                      min:
                        e.target.value === "" ? null : Number(e.target.value),
                      max: range.max,
                    });
                  }}
                  onKeyDown={preventNonNumericValueWhenKeyDown}
                  onPaste={preventNonNumericValueWhenPaste}
                  sx={{ width: "250px" }}
                  inputProps={{
                    min: 0,
                    type: "number",
                    step: "1",
                    ...inputProps,
                  }}
                  label={label}
                />
                <Typography component="span">
                  {unit ? `${unit}以上` : "以上"}
                </Typography>
              </Box>
              <Box
                sx={{
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "flex-start",
                  gap: 1,
                  width: "50px",
                }}
              >
                <Typography component="span">～</Typography>
              </Box>
              <Box
                sx={{
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "flex-start",
                  gap: 1,
                  width: "400px",
                }}
              >
                <TextField
                  error={Boolean(fieldError?.max ?? fieldState.error)}
                  helperText={
                    fieldError?.max?.message ?? fieldState.error?.message
                  }
                  value={range.max ?? ""}
                  onChange={(e) => {
                    onChange({
                      min: range.min,
                      max:
                        e.target.value === "" ? null : Number(e.target.value),
                    });
                  }}
                  onKeyDown={preventNonNumericValueWhenKeyDown}
                  onPaste={preventNonNumericValueWhenPaste}
                  sx={{ width: "250px" }}
                  inputProps={{
                    min: 0,
                    type: "number",
                    step: "1",
                    ...inputProps,
                  }}
                  label={label}
                />
                <Typography component="span">
                  {unit ? `${unit}以下` : "以下"}
                </Typography>
              </Box>
            </Box>
          );
        }}
      />
    </>
  );
};
